import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner, Form, Card } from 'react-bootstrap';
import { 
  FaFilePdf, FaEdit, FaExternalLinkAlt, FaCloudUploadAlt,
  FaMagic, FaCheckCircle, FaCopy, FaDownload
} from 'react-icons/fa';
import pdfFillerService from '../../services/pdfFillerService';
import axiosInstance from '../../helpers/api';

interface PDFFillerEditorProps {
  show: boolean;
  onHide: () => void;
  documentUrl: string;
  documentName: string;
  transactionData?: any;
  onSave?: (documentData: any) => void;
}

const PDFFillerEditor: React.FC<PDFFillerEditorProps> = ({
  show,
  onHide,
  documentUrl,
  documentName,
  transactionData,
  onSave
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadedDoc, setUploadedDoc] = useState<any>(null);
  const [editLink, setEditLink] = useState<string>('');
  const [embedMode, setEmbedMode] = useState(false);

  const uploadToPDFFiller = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, fetch the PDF from the URL
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      const file = new File([blob], documentName, { type: 'application/pdf' });

      // Upload to PDFFiller
      const pdfFillerDoc = await pdfFillerService.uploadAndGetEditLink(file, documentName);
      setUploadedDoc(pdfFillerDoc);

      // Get the LinkToFill URL
      const linkToFillUrl = await pdfFillerService.getLinkToFill(pdfFillerDoc.id);
      setEditLink(linkToFillUrl);

      setSuccess(true);
      setLoading(false);

      // Auto-open in new window if not embedding
      if (!embedMode) {
        window.open(linkToFillUrl, '_blank', 'width=1200,height=800');
      }

    } catch (err) {
      console.error('Error uploading to PDFFiller:', err);
      setError('Failed to upload PDF to PDFFiller. Please try again.');
      setLoading(false);
    }
  };

  const downloadFilledPDF = async () => {
    if (!uploadedDoc) return;

    try {
      setLoading(true);
      const blob = await pdfFillerService.downloadFilledPDF(uploadedDoc.id);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `filled_${documentName}`;
      a.click();
      URL.revokeObjectURL(url);

      // Save to transaction if callback provided
      if (onSave) {
        onSave({ 
          pdfFillerId: uploadedDoc.id,
          editLink,
          filledPdfBlob: blob 
        });
      }

      setLoading(false);
    } catch (err) {
      console.error('Error downloading filled PDF:', err);
      setError('Failed to download filled PDF.');
      setLoading(false);
    }
  };

  const copyEditLink = () => {
    navigator.clipboard.writeText(editLink);
    alert('Edit link copied to clipboard!');
  };

  // Pre-fill fields based on transaction data
  const preFillFields = async () => {
    if (!uploadedDoc || !transactionData) return;

    try {
      setLoading(true);
      
      const fields = [
        { name: 'buyer_name', value: transactionData.client_name || '' },
        { name: 'property_address', value: transactionData.property?.address || '' },
        { name: 'purchase_price', value: transactionData.property?.price || '' },
        { name: 'closing_date', value: transactionData.estimated_closing_date || '' },
        { name: 'agent_name', value: transactionData.agent_name || '' }
      ];

      await pdfFillerService.addFillableFields(uploadedDoc.id, fields);
      
      setLoading(false);
      alert('Transaction data pre-filled successfully!');
    } catch (err) {
      console.error('Error pre-filling fields:', err);
      setError('Failed to pre-fill fields.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && !uploadedDoc) {
      uploadToPDFFiller();
    }
  }, [show]);

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaFilePdf className="me-2 text-danger" />
          PDFFiller Editor - {documentName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && !uploadedDoc && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p>Uploading PDF to PDFFiller...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {uploadedDoc && (
          <>
            <Alert variant="success">
              <FaCheckCircle className="me-2" />
              PDF uploaded successfully! You can now edit it using PDFFiller's professional tools.
            </Alert>

            <Card className="mb-3">
              <Card.Body>
                <h5>Edit Your PDF</h5>
                <p className="text-muted">Click the button below to open PDFFiller's editor in a new window.</p>
                
                <div className="d-flex gap-2 flex-wrap">
                  <Button 
                    variant="primary"
                    onClick={() => window.open(editLink, '_blank')}
                    disabled={!editLink}
                  >
                    <FaExternalLinkAlt className="me-2" />
                    Open PDFFiller Editor
                  </Button>

                  {transactionData && (
                    <Button 
                      variant="outline-primary"
                      onClick={preFillFields}
                      disabled={loading}
                    >
                      <FaMagic className="me-2" />
                      Auto-Fill Transaction Data
                    </Button>
                  )}

                  <Button 
                    variant="outline-secondary"
                    onClick={copyEditLink}
                    disabled={!editLink}
                  >
                    <FaCopy className="me-2" />
                    Copy Edit Link
                  </Button>
                </div>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Body>
                <h5>Instructions</h5>
                <ol>
                  <li>Click "Open PDFFiller Editor" to edit your PDF</li>
                  <li>Click anywhere on the PDF to add text fields</li>
                  <li>Type directly into the PDF - no overlays!</li>
                  <li>Add signatures, checkboxes, and dates as needed</li>
                  <li>Save your changes in PDFFiller</li>
                  <li>Come back here and click "Download Filled PDF"</li>
                </ol>
              </Card.Body>
            </Card>

            {/* Embed option - for future enhancement */}
            {embedMode && (
              <Card>
                <Card.Body>
                  <iframe
                    src={pdfFillerService.getEmbeddedEditorUrl(uploadedDoc.id)}
                    width="100%"
                    height="600px"
                    frameBorder="0"
                    title="PDFFiller Editor"
                  />
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            {editLink && (
              <small className="text-muted">
                Document ID: {uploadedDoc?.id}
              </small>
            )}
          </div>
          <div className="d-flex gap-2">
            {uploadedDoc && (
              <Button 
                variant="success"
                onClick={downloadFilledPDF}
                disabled={loading}
              >
                <FaDownload className="me-2" />
                Download Filled PDF
              </Button>
            )}
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PDFFillerEditor;